import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useInboxDrawerStore} from '../../store/inboxDrawerStore'
import {useChatGraphQL, Conversation, Message} from '../../pages/chat/hooks/useChatGraphQL'
import {resolveConversationChannel} from '../../pages/chat/helpers/channelBadgeHelper'

const drawerStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  width: 380,
  maxWidth: 'calc(100vw - 48px)',
  height: 600,
  maxHeight: 'calc(100vh - 120px)',
  background: '#ffffff',
  borderRadius: 12,
  boxShadow: '0 12px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
  zIndex: 1050,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

const headerStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderBottom: '1px solid #e9ecef',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  flexShrink: 0,
}

const tabsRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  padding: '8px 12px',
  borderBottom: '1px solid #f1f3f5',
  flexShrink: 0,
}

const listStyle: React.CSSProperties = {flex: 1, minHeight: 0, overflowY: 'auto'}

const messagesAreaStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '12px',
  background: '#f7f8fb',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}

const composerStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderTop: '1px solid #e9ecef',
  display: 'flex',
  alignItems: 'flex-end',
  gap: 8,
  flexShrink: 0,
  background: '#fff',
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 14px',
  borderBottom: '1px solid #f1f3f5',
  cursor: 'pointer',
  background: '#fff',
  border: 'none',
  width: '100%',
  textAlign: 'left',
}

const avatarStyle = (color: string, size = 38): React.CSSProperties => ({
  width: size,
  height: size,
  flexShrink: 0,
  borderRadius: '50%',
  background: color,
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '0.8rem',
})

const PALETTE = ['#0d6efd', '#198754', '#dc3545', '#fd7e14', '#6f42c1', '#20c997', '#0dcaf0']
const colorFor = (key: number | string) => {
  const text = String(key)
  let hash = 0
  for (let i = 0; i < text.length; i++) hash = (hash << 5) - hash + text.charCodeAt(i)
  return PALETTE[Math.abs(hash) % PALETTE.length]
}
const initials = (name: string) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || '#'

const formatTime = (iso?: string | null) => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
}

export const InboxDrawer: React.FC = () => {
  const open = useInboxDrawerStore((state) => state.open)
  const close = useInboxDrawerStore((state) => state.close)

  if (!open) return null
  return <InboxDrawerContent onClose={close} />
}

const InboxDrawerContent: React.FC<{onClose: () => void}> = ({onClose}) => {
  const navigate = useNavigate()
  const {
    conversations,
    currentConversation,
    setCurrentConversation,
    messages,
    loadingInbox,
    loadingMessages,
    sendMessage,
    channelUnread,
    reloadInbox,
  } = useChatGraphQL()

  const [channel, setChannel] = useState<'WHATSAPP' | 'SMS'>('WHATSAPP')
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState('')
  const messagesEndRef = useRef<HTMLDivElement | null>(null)

  // Refresh the inbox each time the widget opens.
  useEffect(() => {
    reloadInbox()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    return conversations.filter((c) => {
      if (resolveConversationChannel(c) !== channel) return false
      if (!normalized) return true
      const haystack = [c.full_name, c.phone, c.last_message?.content || ''].join(' ').toLowerCase()
      return haystack.includes(normalized)
    })
  }, [conversations, channel, query])

  const threadMessages = useMemo(
    () => messages.filter((m) => m.channel === 'WHATSAPP' || m.channel === 'SMS'),
    [messages]
  )

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'})
  }, [threadMessages, currentConversation])

  const openFull = () => {
    onClose()
    navigate('/inbox')
  }

  const handleSend = async () => {
    if (!currentConversation || !draft.trim()) return
    const text = draft.trim()
    setDraft('')
    await sendMessage(currentConversation.id, text, channel)
  }

  const tabs: {key: 'WHATSAPP' | 'SMS'; label: string; icon: string; color: string; unread: number}[] = [
    {key: 'WHATSAPP', label: 'WhatsApp', icon: 'bi-whatsapp', color: '#50cd89', unread: channelUnread?.whatsapp || 0},
    {key: 'SMS', label: 'SMS', icon: 'bi-chat-dots', color: '#3e97ff', unread: channelUnread?.sms || 0},
  ]

  return (
    <div style={drawerStyle} id='global-inbox-drawer'>
      {/* Header */}
      <div style={headerStyle}>
        {currentConversation ? (
          <>
            <button
              className='btn btn-sm btn-icon btn-light'
              onClick={() => setCurrentConversation(null)}
              title='Back'
            >
              <i className='bi bi-arrow-left' />
            </button>
            <div className='flex-grow-1 min-w-0'>
              <div className='fw-bold text-truncate' style={{fontSize: '0.9rem'}}>
                {currentConversation.full_name || currentConversation.phone}
              </div>
              {currentConversation.phone && (
                <div className='text-muted text-truncate' style={{fontSize: '0.72rem'}}>
                  {currentConversation.phone}
                </div>
              )}
            </div>
            <button className='btn btn-sm btn-light' onClick={openFull} title='Open full inbox'>
              <i className='bi bi-box-arrow-up-right' />
            </button>
            <button className='btn btn-sm btn-icon btn-light' onClick={onClose} title='Close'>
              <i className='bi bi-x-lg' />
            </button>
          </>
        ) : (
          <>
            <div className='fw-bold d-flex align-items-center gap-2' style={{fontSize: '0.95rem'}}>
              <i className='bi bi-chat-square-text-fill text-primary' /> Inbox
            </div>
            <div className='d-flex align-items-center gap-1'>
              <button className='btn btn-sm btn-light' onClick={openFull} title='Open full inbox'>
                <i className='bi bi-box-arrow-up-right' />
              </button>
              <button className='btn btn-sm btn-icon btn-light' onClick={onClose} title='Close'>
                <i className='bi bi-x-lg' />
              </button>
            </div>
          </>
        )}
      </div>

      {currentConversation ? (
        /* ── Conversation view ─────────────────────────────────────────── */
        <>
          <div style={messagesAreaStyle}>
            {loadingMessages && threadMessages.length === 0 ? (
              <div className='text-center text-muted my-4' style={{fontSize: '0.8rem'}}>
                Loading…
              </div>
            ) : threadMessages.length === 0 ? (
              <div className='text-center text-muted my-4' style={{fontSize: '0.8rem'}}>
                No messages yet
              </div>
            ) : (
              threadMessages.map((m: Message) => {
                const outbound = m.direction === 'OUTBOUND'
                return (
                  <div
                    key={m.id}
                    style={{
                      alignSelf: outbound ? 'flex-end' : 'flex-start',
                      maxWidth: '80%',
                      background: outbound ? '#d9fdd3' : '#ffffff',
                      border: '1px solid #eceff3',
                      borderRadius: 10,
                      padding: '6px 9px',
                      fontSize: '0.82rem',
                      color: '#181c32',
                      wordBreak: 'break-word',
                    }}
                  >
                    <div>{m.content}</div>
                    <div className='text-muted' style={{fontSize: '0.62rem', textAlign: 'right', marginTop: 2}}>
                      {formatTime(m.created_at)}
                    </div>
                  </div>
                )
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          <div style={composerStyle}>
            <textarea
              className='form-control form-control-sm'
              rows={1}
              value={draft}
              placeholder={`Reply via ${channel === 'WHATSAPP' ? 'WhatsApp' : 'SMS'}…`}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void handleSend()
                }
              }}
              style={{resize: 'none', maxHeight: 90}}
            />
            <button className='btn btn-sm btn-primary' onClick={() => void handleSend()} disabled={!draft.trim()}>
              <i className='bi bi-send-fill' />
            </button>
          </div>
        </>
      ) : (
        /* ── List view ─────────────────────────────────────────────────── */
        <>
          <div style={tabsRowStyle}>
            {tabs.map((tab) => {
              const active = channel === tab.key
              return (
                <button
                  key={tab.key}
                  onClick={() => setChannel(tab.key)}
                  className='btn btn-sm'
                  style={{
                    flex: 1,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    borderRadius: 999,
                    background: active ? tab.color : '#f4f6fb',
                    color: active ? '#fff' : '#7e8299',
                    border: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5,
                  }}
                >
                  <i className={`bi ${tab.icon}`} />
                  {tab.label}
                  {tab.unread > 0 && (
                    <span
                      style={{
                        background: active ? 'rgba(255,255,255,0.3)' : '#f1416c',
                        color: '#fff',
                        borderRadius: 999,
                        fontSize: '0.6rem',
                        padding: '0 5px',
                        lineHeight: '15px',
                      }}
                    >
                      {tab.unread}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div style={{padding: '8px 12px', flexShrink: 0}}>
            <input
              className='form-control form-control-sm'
              placeholder='Search by name or phone…'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div style={listStyle}>
            {loadingInbox && filtered.length === 0 ? (
              <div className='text-center text-muted my-4' style={{fontSize: '0.8rem'}}>
                Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className='text-center text-muted my-4' style={{fontSize: '0.8rem'}}>
                No conversations
              </div>
            ) : (
              filtered.map((c: Conversation) => (
                <button key={c.id} style={rowStyle} onClick={() => setCurrentConversation(c)}>
                  <span style={avatarStyle(colorFor(c.id))}>{initials(c.full_name || c.phone)}</span>
                  <span className='flex-grow-1 min-w-0'>
                    <span className='d-flex justify-content-between align-items-center gap-2'>
                      <span className='fw-semibold text-truncate' style={{fontSize: '0.85rem'}}>
                        {c.full_name || c.phone}
                      </span>
                      <span className='text-muted flex-shrink-0' style={{fontSize: '0.65rem'}}>
                        {formatTime(c.last_message?.created_at)}
                      </span>
                    </span>
                    <span className='d-flex justify-content-between align-items-center gap-2'>
                      <span className='text-muted text-truncate' style={{fontSize: '0.75rem'}}>
                        {c.last_message?.content || c.phone}
                      </span>
                      {c.unread_count > 0 && (
                        <span
                          className='flex-shrink-0'
                          style={{
                            background: '#f1416c',
                            color: '#fff',
                            borderRadius: 999,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            minWidth: 16,
                            height: 16,
                            padding: '0 5px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {c.unread_count}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}
